import {
  IConversationTimelineObject,
  ITimelineOrder,
  ITimelineOrderGroup,
  TimelineObjectType,
  isTimelineOrder,
} from '../models/timeline.models';

/**
 * Collapse the orders one create-order run produced into a single timeline
 * artifact (WB-7598).
 *
 * One run — an AI multi-order PDF split (WB-7372) or a segregation-rule split
 * (WB-7566) — links every order it produces to the same message, so `message_id`
 * is the group key for both cases at once.
 *
 * Grouping is deliberately **not** limited to adjacent entries: the run is what
 * the artifact represents, so two orders sharing a `message_id` belong together
 * however the payload interleaves them with messages, events or comments.
 * The group takes the position of its first member, which keeps the timeline's
 * `created_at` ordering intact.
 *
 * Left alone: a `message_id` carried by a single order, and a null/absent one
 * (a thread created before WB-7599 shipped). Both keep rendering the individual
 * card they always did — grouping must never be the reason a card disappears.
 */
export function groupTimelineOrders(
  objects: IConversationTimelineObject[]
): IConversationTimelineObject[] {
  const membersByMessageId = new Map<number, ITimelineOrder[]>();

  for (const obj of objects) {
    if (!isTimelineOrder(obj)) continue;
    const messageId = obj.message_id;
    if (!messageId) continue;
    const members = membersByMessageId.get(messageId);
    if (members) members.push(obj);
    else membersByMessageId.set(messageId, [obj]);
  }

  // A run that produced one order is not a group — it keeps the individual card
  // it always had, so only 2+ members get an artifact built.
  const groupByMessageId = new Map<number, ITimelineOrderGroup>();
  for (const [messageId, members] of membersByMessageId) {
    if (members.length > 1) groupByMessageId.set(messageId, buildOrderGroup(messageId, members));
  }

  if (!groupByMessageId.size) return objects;

  return objects.reduce<IConversationTimelineObject[]>((acc, obj) => {
    const group =
      isTimelineOrder(obj) && obj.message_id ? groupByMessageId.get(obj.message_id) : undefined;

    // Every member resolves to the same group; only the first one emits it, so
    // the artifact lands where the earliest order of the run sat.
    if (!group) acc.push(obj);
    else if (group.orders[0] === obj) acc.push(group);
    return acc;
  }, []);
}

function buildOrderGroup(messageId: number, orders: ITimelineOrder[]): ITimelineOrderGroup {
  return {
    object_type: TimelineObjectType.ORDER_GROUP,
    id: `order-group-${messageId}`,
    message_id: messageId,
    orders,
  };
}
